import React, { useState, useEffect, useCallback } from 'react';
import {
  Users, Plus, Pencil, X, Check, RefreshCw, AlertCircle,
  ShieldOff, KeyRound, Ban, CheckCircle2, Shield,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { NavBar } from './NavBar';

// ─── Types ────────────────────────────────────────────────────────────────────

interface CompanyUser {
  username:            string;
  email:               string;
  name:                string;
  phone_number:        string;
  can_manage_mappings: string;
  status:              string;
  enabled:              boolean;
  groups:               string[];
}

interface UserFormState {
  email:              string;
  name:               string;
  phone_number:       string;
  is_company_admin:   boolean;
  can_manage_mappings: boolean;
}

const EMPTY_FORM: UserFormState = {
  email: '', name: '', phone_number: '', is_company_admin: false, can_manage_mappings: false,
};

export function ManageUsers() {
  const { user, getToken, getGroups } = useAuth();
  const API_URL = process.env.REACT_APP_API_URL ?? '';

  const [checkingAccess, setCheckingAccess] = useState(true);
  const [isAdmin,        setIsAdmin]        = useState(false);

  const [users,   setUsers]   = useState<CompanyUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);
  const [actionMsg, setActionMsg] = useState<{ type: 'ok' | 'error'; text: string } | null>(null);

  const [showForm,    setShowForm]    = useState(false);
  const [editingUser, setEditingUser] = useState<string | null>(null); // username being edited, null = adding
  const [form,        setForm]        = useState<UserFormState>(EMPTY_FORM);
  const [saving,      setSaving]      = useState(false);
  const [formError,   setFormError]   = useState<string | null>(null);

  const callApi = useCallback(async (action: string, extra: Record<string, any> = {}) => {
    const token = await getToken();
    const res = await fetch(`${API_URL}/admin/users`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ action, ...extra }),
    });
    const result = await res.json();
    if (!res.ok) throw new Error(result.error ?? `HTTP ${res.status}`);
    return result;
  }, [API_URL, getToken]);

  // Check admin access via group membership on mount
  useEffect(() => {
    const check = async () => {
      try {
        const groups = await getGroups();
        setIsAdmin(groups.includes('company-admin'));
      } catch {
        setIsAdmin(false);
      } finally {
        setCheckingAccess(false);
      }
    };
    check();
  }, [getGroups]);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await callApi('list_users');
      setUsers(result.users ?? []);
    } catch (e: any) {
      setError(e.message ?? 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [callApi]);

  useEffect(() => {
    if (isAdmin) loadUsers();
  }, [isAdmin, loadUsers]);

  const openAddForm = () => {
    setEditingUser(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setShowForm(true);
  };

  const openEditForm = (u: CompanyUser) => {
    setEditingUser(u.username);
    setForm({
      email: u.email,
      name: u.name,
      phone_number: u.phone_number,
      is_company_admin: u.groups.includes('company-admin'),
      can_manage_mappings: u.can_manage_mappings === 'true',
    });
    setFormError(null);
    setShowForm(true);
  };

  const closeForm = () => { setShowForm(false); setFormError(null); };

  const saveUser = async () => {
    setSaving(true);
    setFormError(null);
    try {
      if (editingUser) {
        await callApi('update_user', {
          username: editingUser,
          name: form.name,
          phone_number: form.phone_number,
          is_company_admin: form.is_company_admin,
          can_manage_mappings: form.can_manage_mappings,
        });
        setActionMsg({ type: 'ok', text: `Updated ${form.name || editingUser}.` });
      } else {
        await callApi('create_user', {
          email: form.email,
          name: form.name,
          phone_number: form.phone_number,
          is_company_admin: form.is_company_admin,
          can_manage_mappings: form.can_manage_mappings,
        });
        setActionMsg({ type: 'ok', text: `Invited ${form.email} — they'll receive an email with a temporary password.` });
      }
      setShowForm(false);
      await loadUsers();
    } catch (e: any) {
      setFormError(e.message ?? 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async (u: CompanyUser) => {
    setActionMsg(null);
    try {
      await callApi('set_user_enabled', { username: u.username, enabled: !u.enabled });
      setActionMsg({ type: 'ok', text: `${u.enabled ? 'Disabled' : 'Enabled'} ${u.name || u.email}.` });
      await loadUsers();
    } catch (e: any) {
      setActionMsg({ type: 'error', text: e.message ?? 'Failed to update user' });
    }
  };

  const resetPassword = async (u: CompanyUser) => {
    if (!window.confirm(`Send a password reset code to ${u.email}?`)) return;
    setActionMsg(null);
    try {
      await callApi('reset_user_password', { username: u.username });
      setActionMsg({ type: 'ok', text: `Password reset code sent to ${u.email}.` });
    } catch (e: any) {
      setActionMsg({ type: 'error', text: e.message ?? 'Failed to reset password' });
    }
  };

  // ── Access restricted ─────────────────────────────────────────────────────
  if (checkingAccess) {
    return (
      <div className="min-h-screen bg-gray-50">
        <NavBar />
        <main className="max-w-2xl mx-auto px-4 py-20 text-center text-gray-400">
          <RefreshCw className="w-8 h-8 mx-auto mb-3 animate-spin opacity-50" />
        </main>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50">
        <NavBar />
        <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <ShieldOff className="w-14 h-14 mx-auto mb-4 text-gray-300" />
          <h1 className="text-lg font-bold text-gray-900">Access Restricted</h1>
          <p className="text-sm text-gray-500 mt-2">
            You don't have permission to manage users. Contact your company administrator if you believe this is an error.
          </p>
        </main>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50">
      <NavBar />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-5">

        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-10 h-10 bg-slate-700 rounded-lg">
              <Users className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Manage Users</h1>
              <p className="text-sm text-gray-500">Users at {user?.['custom:company_name'] ?? 'your company'}</p>
            </div>
          </div>
          <button
            onClick={openAddForm}
            className="flex items-center space-x-2 px-4 py-2 bg-slate-700 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-colors"
          >
            <Plus className="w-4 h-4" /><span>Add User</span>
          </button>
        </div>

        {actionMsg && (
          <div className={`flex items-start space-x-2 rounded-lg px-4 py-2.5 ${actionMsg.type === 'ok' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            {actionMsg.type === 'ok'
              ? <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
              : <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />}
            <p className={`text-sm ${actionMsg.type === 'ok' ? 'text-green-700' : 'text-red-700'}`}>{actionMsg.text}</p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Add/Edit form */}
        {showForm && (
          <div className="bg-white rounded-xl border border-slate-300 shadow-sm p-5 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900">{editingUser ? `Edit ${form.name || form.email}` : 'Invite New User'}</h3>
              <button onClick={closeForm}><X className="w-4 h-4 text-gray-400 hover:text-gray-600" /></button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                <input
                  type="email" value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  disabled={!!editingUser}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500 disabled:bg-gray-100 disabled:text-gray-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Name</label>
                <input
                  type="text" value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Phone</label>
                <input
                  type="text" value={form.phone_number} placeholder="+15551234567"
                  onChange={e => setForm(f => ({ ...f, phone_number: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
                />
              </div>
            </div>

            <div className="flex items-center space-x-6 pt-1">
              <label className="flex items-center space-x-2 text-sm text-gray-700">
                <input
                  type="checkbox" checked={form.is_company_admin}
                  onChange={e => setForm(f => ({ ...f, is_company_admin: e.target.checked }))}
                  className="rounded border-gray-300 text-slate-700 focus:ring-slate-500"
                />
                <span>Company Admin</span>
              </label>
              <label className="flex items-center space-x-2 text-sm text-gray-700">
                <input
                  type="checkbox" checked={form.can_manage_mappings}
                  onChange={e => setForm(f => ({ ...f, can_manage_mappings: e.target.checked }))}
                  className="rounded border-gray-300 text-slate-700 focus:ring-slate-500"
                />
                <span>Can Manage Code Mappings</span>
              </label>
            </div>

            {formError && (
              <div className="flex items-start space-x-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-red-700">{formError}</p>
              </div>
            )}

            <div className="flex items-center space-x-2">
              <button
                onClick={saveUser}
                disabled={saving || !form.name.trim() || (!editingUser && !form.email.trim())}
                className="flex items-center space-x-2 px-4 py-2 bg-slate-700 text-white text-sm font-medium rounded-lg hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                <span>{saving ? 'Saving…' : editingUser ? 'Save Changes' : 'Send Invitation'}</span>
              </button>
              <button onClick={closeForm} className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors">
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* User table */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {loading ? (
            <div className="text-center py-16 text-gray-400">
              <RefreshCw className="w-8 h-8 mx-auto mb-3 animate-spin opacity-50" />
              <p className="text-sm">Loading users…</p>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <p className="text-sm">No users found.</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  {['Name', 'Email', 'Phone', 'Roles', 'Status', 'Actions'].map(col => (
                    <th key={col} className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {users.map(u => (
                  <tr key={u.username} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-2.5 font-medium text-gray-900 whitespace-nowrap">{u.name || '—'}</td>
                    <td className="px-4 py-2.5 text-gray-600 whitespace-nowrap">{u.email}</td>
                    <td className="px-4 py-2.5 text-gray-500 whitespace-nowrap">{u.phone_number || '—'}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex flex-wrap gap-1">
                        {u.groups.includes('company-admin') && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                            <Shield className="w-3 h-3" />Admin
                          </span>
                        )}
                        {u.can_manage_mappings === 'true' && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">Mappings</span>
                        )}
                        {!u.groups.includes('company-admin') && u.can_manage_mappings !== 'true' && (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${u.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500'}`}>
                        {u.enabled ? 'Active' : 'Disabled'}
                      </span>
                      {u.status === 'FORCE_CHANGE_PASSWORD' && (
                        <span className="ml-1 text-xs text-yellow-600">(Invited)</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center space-x-1">
                        <button onClick={() => openEditForm(u)} title="Edit" className="p-1.5 text-gray-400 hover:text-slate-700 hover:bg-gray-100 rounded transition-colors">
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => resetPassword(u)} title="Reset password" className="p-1.5 text-gray-400 hover:text-slate-700 hover:bg-gray-100 rounded transition-colors">
                          <KeyRound className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => toggleEnabled(u)}
                          title={u.enabled ? 'Disable' : 'Enable'}
                          className={`p-1.5 rounded transition-colors ${u.enabled ? 'text-gray-400 hover:text-red-600 hover:bg-red-50' : 'text-gray-400 hover:text-green-600 hover:bg-green-50'}`}
                        >
                          {u.enabled ? <Ban className="w-3.5 h-3.5" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </div>
  );
}
